import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api";
import { createResetPasswordSchema } from "../schemas/reset-password.schema";
import type {
  ResetPasswordFormMethods,
  ResetPasswordFormValues,
} from "../types/reset-password.types";

interface UseResetPasswordReturn {
  form: ResetPasswordFormMethods;
  loading: boolean;
  validating: boolean;
  error: string;
  retryAfterSeconds: number | null;
  passwordChanged: boolean;
  isTokenValid: boolean;
  displayError: string;
  submitResetPassword: (values: ResetPasswordFormValues) => Promise<void>;
}

interface ResetPasswordError {
  message?: string;
  response?: {
    status?: number;
  };
}

const isResetPasswordError = (error: unknown): error is ResetPasswordError =>
  typeof error === "object" && error !== null;

const extractRetryAfterSeconds = (message: string | undefined | null): number | null => {
  if (!message) return null;

  const match = message.match(/(\d+)\s*seconds?/i);
  if (!match?.[1]) return null;

  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

const formatRetryMessage = (message: string, seconds: number) =>
  message.replace(/\d+\s*seconds?/i, `${seconds} second${seconds === 1 ? "" : "s"}`);

export const useResetPassword = (token: string | null): UseResetPasswordReturn => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const resetPasswordSchema = createResetPasswordSchema(t);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState("");
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);

  const validateResetToken = useCallback(
    async (resetToken: string) => {
      setValidating(true);

      try {
        const response = await authApi.validateResetToken(resetToken);

        if (response?.valid) {
          setIsTokenValid(true);
          setError("");
          return;
        }

        setIsTokenValid(false);
        setError(t("auth.reset_password_invalid_link"));
      } catch {
        setIsTokenValid(false);
        setError(t("auth.reset_password_invalid_link"));
      } finally {
        setValidating(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!token) {
      navigate("/forgot-password");
      return;
    }

    void validateResetToken(token);
  }, [navigate, token, validateResetToken]);

  useEffect(() => {
    if (retryAfterSeconds === null) {
      return;
    }

    if (retryAfterSeconds <= 0) {
      setRetryAfterSeconds(null);
      setCooldownMessage("");
      return;
    }

    const timer = window.setInterval(() => {
      setRetryAfterSeconds((current) => {
        if (current === null) {
          return null;
        }

        if (current <= 1) {
          window.clearInterval(timer);
          setCooldownMessage("");
          return null;
        }

        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [retryAfterSeconds]);

  const submitResetPassword = useCallback(
    async (values: ResetPasswordFormValues) => {
      if (!token || !isTokenValid) {
        return;
      }

      setLoading(true);
      setError("");
      setCooldownMessage("");
      setRetryAfterSeconds(null);

      try {
        const response = await authApi.resetPasswordWithToken({
          token,
          new_password: values.newPassword,
          new_password_confirmation: values.confirmPassword,
        });

        if (response) {
          setPasswordChanged(true);
          form.reset();
          return;
        }
      } catch (error: unknown) {
        const responseMessage = isResetPasswordError(error) ? error.message : undefined;
        const retrySeconds = extractRetryAfterSeconds(responseMessage);

        if (
          isResetPasswordError(error) &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          setError(t("auth.reset_password_expired"));
          window.setTimeout(() => {
            navigate("/forgot-password");
          }, 3000);
        } else if (retrySeconds) {
          const retryMessage = responseMessage || t("auth.reset_password_retry");
          setCooldownMessage(retryMessage);
          setRetryAfterSeconds(retrySeconds);
          setError(formatRetryMessage(retryMessage, retrySeconds));
        } else if (responseMessage === "Network Error") {
          setError(t("auth.check_internet_connection"));
        } else if (responseMessage) {
          setError(responseMessage);
        } else {
          setError(t("auth.password_change_failed"));
        }
      } finally {
        setLoading(false);
      }
    },
    [form, isTokenValid, navigate, t, token],
  );

  const displayError =
    retryAfterSeconds !== null && cooldownMessage
      ? formatRetryMessage(cooldownMessage, retryAfterSeconds)
      : error;

  return {
    form,
    loading,
    validating,
    error,
    retryAfterSeconds,
    passwordChanged,
    isTokenValid,
    displayError,
    submitResetPassword,
  };
};
